import { COMPATIBILITY_LEVELS, getCompatibilityLevelFromInternalRating } from './restaurantUtils';

/**
 * Tier word per `COMPATIBILITY_LEVELS` index, derived from that array's own
 * `primaryLabel` (stripping the trailing " TasteMatch") rather than
 * `secondaryLabel`, so the dropdown can never drift from the app-wide scale.
 * This also keeps the bottom tier's word as "No" (from primaryLabel "No
 * TasteMatch") instead of secondaryLabel's "None" — CD's final call: the
 * bottom band reads "No TasteMatch", the same wording every other
 * compatibility surface uses. Every other tier's stripped primaryLabel
 * already matches its secondaryLabel, so this is a single-field derivation,
 * not a per-tier special case.
 */
const TIER_WORDS = COMPATIBILITY_LEVELS.map(level => level.primaryLabel.replace(/ TasteMatch$/, ''));

export type TasteMatchBandScoreSource = 'taste_match' | 'self_rating';

export interface TasteMatchBand {
  /** e.g. "Low", "Medium", "Highest" — never rendered bare, see `label`. */
  tierWord: string;
  /** Hex color for this band, from `COMPATIBILITY_LEVELS`. */
  color: string;
  /**
   * Visible chip text AND the per-row `aria-label` text — the same string
   * for both channels, with no ™ symbol (that belongs on the suggestions
   * group's own `aria-label`, once per open dropdown, not per row).
   */
  label: string;
}

/**
 * Derives a TasteMatch band (tier word, color, paired label) from a row's
 * signed −100..100 internal rating.
 *
 * `rating` MUST already be on the internal −100..100 scale (`Restaurant
 * .rating` / the autocomplete row's `rating` field) — never a
 * RecommendationOrchestrator 0-100 score. `getInternalRatingFromRecommendationScore`
 * exists to convert that separate scale onto this one; applying it to an
 * already-internal-scale rating here would silently double-transform and
 * band the row on the wrong scale entirely. Do not call it in this file.
 */
export function getTasteMatchBand(
  rating: number,
  scoreSource: TasteMatchBandScoreSource
): TasteMatchBand {
  const level = getCompatibilityLevelFromInternalRating(rating);
  const tierWord = TIER_WORDS[level] ?? TIER_WORDS[0];
  const color = COMPATIBILITY_LEVELS[level]?.primaryColor ?? COMPATIBILITY_LEVELS[0].primaryColor;
  const label = scoreSource === 'taste_match' ? `${tierWord} TasteMatch` : `Rated ${tierWord}`;

  return { tierWord, color, label };
}
